import type { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupedEstimationSelect } from '@/components/common/estimation/GroupedEstimationSelect';
import type { EstimationOption } from '@/components/common/estimation/useGroupedEstimationOptions';
import { useGroupedEstimationOptions } from '@/components/common/estimation/useGroupedEstimationOptions';
import { TIMELINE_ITEM_IMPUTED_FIELD } from '@/lib/api/types/loop-planner/timeline-item';
import { useTableCellMutation } from '../hooks/useTableCellMutation';
import type { ItemsTableRow } from '../types';

const GroupedEstimationEditCell = ({
  id,
  row,
  field,
  api,
}: GridRenderEditCellParams<ItemsTableRow>) => {
  const cellId = `estimation-${id}-${field}`;
  const { t } = useTranslation();
  const itemId = Number(id);

  const { groupedOptions, flatOptions, isLoading } = useGroupedEstimationOptions();
  const { commit, isPending } = useTableCellMutation();

  const selectedOption = useMemo(() => {
    if (!row.estimationId) {
      return null;
    }
    return flatOptions.find((opt) => opt.value === row.estimationId) ?? null;
  }, [row.estimationId, flatOptions]);

  const isImputed = row.imputedFields?.includes(TIMELINE_ITEM_IMPUTED_FIELD.ESTIMATION) ?? false;

  const handleChange = useCallback(
    (opt: EstimationOption | null) => {
      commit({
        itemId,
        api,
        row,
        data: { estimationId: opt ? opt.value : null },
      });
    },
    [commit, itemId, api, row],
  );

  return (
    <GroupedEstimationSelect
      id={cellId}
      name={cellId}
      cellEdit
      autoFocus
      openMenuOnFocus
      menuPlacement="auto"
      menuPortalTarget={document.body}
      value={selectedOption}
      onChange={handleChange}
      groupedOptions={groupedOptions}
      placeholder={t(($) => $.adaptivePlanner.itemPanel.selectPlaceholders.estimation)}
      isDisabled={isPending}
      isLoading={isLoading}
      isImputed={isImputed}
    />
  );
};

export const EstimationEditCell = (props: GridRenderEditCellParams<ItemsTableRow>) => {
  return <GroupedEstimationEditCell {...props} />;
};
